import { channelBadgeKey } from '../../channel-badge.js';
import type { MastheadModel } from '../../home/compose-home.js';
import { useT } from '../../i18n/LocaleProvider.js';
import { ctaText, itemText } from './labels.js';

/**
 * masthead — 대상 채널의 프리뷰(또는 포스터)와 정보 패널 (`docs/63` §2.1 · §5).
 *
 * **CTA 가 DOM 의 첫 후보다** — `autoFocus` 를 두고(옛 게이트의 「채널 시청 시작」과 같다)
 * `data-testid="start-watching"` 을 잇는다: 「이 문서에서 소리 있는 재생을 처음 여는 버튼」
 * (`docs/63` §6.6). 옛 게이트와 홈은 동시에 그려지지 않는다.
 *
 * 프리뷰 `<video>` 는 **여기서 만들지 않는다** — `use-home-preview.ts` 가 앱이 들고 있는
 * 한 쌍 중 하나를 `previewHost` 에 넣는다(절대 규칙 82). 포스터는 그 위에 겹치고, 프리뷰가
 * 실제로 그림을 붙였을 때만 숨는다.
 */
export function Masthead({
  model,
  previewHost,
  previewShowing,
  autoFocus,
  onWatch,
}: {
  model: MastheadModel;
  /** 프리뷰 엘리먼트가 들어갈 자리. `preview` 모드에서만 쓴다 */
  previewHost: (el: HTMLDivElement | null) => void;
  /** 프리뷰가 첫 프레임을 붙였다 — 그때만 포스터를 숨긴다 (근거 없는 표시는 틀린 것보다 나쁘다) */
  previewShowing: boolean;
  /** 초기 포커스 — 옛 게이트와 같다. **모바일(터치)에서는 두지 않는다**(스크롤이 튄다 · §2.3) */
  autoFocus: boolean;
  onWatch: () => void;
}) {
  const t = useT();
  if (model.kind === 'empty') {
    return (
      <section className="home__masthead home__masthead--empty" data-testid="home-masthead">
        <p className="home__empty">{t('channels.emptyTitle')}</p>
      </section>
    );
  }
  if (model.kind === 'creative') {
    // 2단계의 자리 — 지금은 만들어지지 않는다(`composeHome` 이 내지 않는다 · D3).
    return null;
  }
  const { channel } = model.row;
  const onair = channel.delivery === 'onair';
  const badgeKey = channelBadgeKey(channel);
  return (
    <section
      className="home__masthead"
      data-testid="home-masthead"
      data-masthead-kind={model.kind}
      data-channel-id={channel.channelId}
    >
      <div className="home__visual">
        <div className="home__preview-host" ref={previewHost} />
        {model.poster !== null && (
          <img
            className={`home__poster${previewShowing ? ' home__poster--hidden' : ''}`}
            src={model.poster}
            alt=""
          />
        )}
      </div>
      <div className="home__info">
        <p className="home__channel" data-i18n="data">
          <span className="home__number">{channel.number}</span> {channel.name}{' '}
          {onair ? (
            <span className="home__badge home__badge--onair">{t('onair.badge')}</span>
          ) : (
            // v33-g. 일반 채널은 **장르**가 배지다 — 없으면 그리지 않는다(`channel-badge.ts`)
            badgeKey !== null && <span className="home__badge">{t(badgeKey)}</span>
          )}
        </p>
        {model.now !== null && (
          <p className="home__line">
            <span className="home__label">{onair ? t('onair.live') : t('epg.entryPoint')}</span>{' '}
            <span data-i18n="data">{itemText(t, model.now)}</span>
          </p>
        )}
        {model.next.length > 0 && (
          <p className="home__line home__line--next">
            <span className="home__label">{t('home.next')}</span>{' '}
            <span data-i18n="data">{model.next.map((item) => itemText(t, item)).join(' · ')}</span>
          </p>
        )}
        <button
          type="button"
          className="gate__button home__cta"
          data-testid="start-watching"
          autoFocus={autoFocus}
          onClick={onWatch}
        >
          {ctaText(t, model.label, model.now)}
        </button>
        <p className="home__hint">{t('home.pressOk')}</p>
      </div>
    </section>
  );
}
